import React from 'react';
import { Switch } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { EyeOutlined } from '@ant-design/icons';
import { ButtonWrapper } from 'components';
import { Text } from 'atoms';

// Constants
export const DATE_FORMAT = 'DD-MM-YYYY';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY, HH:mm';

// Filter Options
export const StatusOptions = [
	{ value: true, label: 'Active' },
	{ value: false, label: 'Inactive' },
];

export const VendorTypeOptions = [
	{ value: 'temporary', label: 'Temporary' },
	{ value: 'regular', label: 'Regular' },
];

export const LegalStructureOptions = [
	{ value: 'Company', label: 'Company' },
	{ value: 'LLP', label: 'LLP' },
	{ value: 'Individual', label: 'Individual' },
	{ value: 'Aggregator', label: 'Aggregator' },
];

export const ComplianceStatusOptions = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'verified', label: 'Verified' },
	{ value: 'rejected', label: 'Rejected' },
];

// Styled Components
const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// Table Columns Configuration
export const getTableColumns = (navigate, handleStatusToggle) => [
	{
		title: 'Vendor Name',
		dataIndex: 'vendor_name',
		key: 'vendor_name',
		sorter: true,
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Vendor Type',
		dataIndex: 'vendor_type',
		key: 'vendor_type',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Legal Structure',
		dataIndex: 'legal_structure',
		key: 'legal_structure',
		render: (text, record) => {
			const legalStructure = record?.vendor_info?.legal_structure || text;
			return <Text>{legalStructure || '-'}</Text>;
		},
	},
	{
		title: 'PoC Name',
		dataIndex: 'poc_name',
		key: 'poc_name',
		render: (text, record) => {
			if (!record) return <Text>-</Text>;
			// API structure: contact_info.primary.name
			const pocName =
				text ||
				record?.contact_info?.primary?.name ||
				record?.contact_info?.primary_poc_name ||
				record?.primary_poc_name;
			return <Text>{pocName || '-'}</Text>;
		},
	},
	{
		title: 'PoC Number',
		dataIndex: 'poc_number',
		key: 'poc_number',
		render: (text, record) => {
			if (!record) return <Text>-</Text>;
			// API structure: contact_info.primary.contact
			const pocNumber =
				text ||
				record?.contact_info?.primary?.contact ||
				record?.contact_info?.primary_poc_contact ||
				record?.primary_poc_contact;
			return <Text>{pocNumber || '-'}</Text>;
		},
	},
	{
		title: 'Address',
		dataIndex: 'address',
		key: 'address',
		render: (text, record) => {
			if (!record) return <Text>-</Text>;

			// Primary: Use vendor_info.address.address_line from response
			const addressLine = record?.vendor_info?.address?.address_line;
			if (addressLine) {
				return (
					<Text style={{ maxWidth: '200px', whiteSpace: 'normal' }}>{addressLine}</Text>
				);
			}

			// Fallback: Build address from vendor_address fields
			const address = record?.vendor_address;
			if (address && typeof address === 'object') {
				const parts = [
					address?.address_line1,
					address?.city,
					address?.state,
					address?.pincode,
				].filter((part) => part !== null && part !== undefined && part !== '');
				const addressString = parts.length > 0 ? parts.join(', ') : '-';
				return (
					<Text style={{ maxWidth: '200px', whiteSpace: 'normal' }}>{addressString}</Text>
				);
			}

			// Final fallback to old structure
			const fallbackAddress =
				text ||
				record?.vendor_info?.address?.address_line1 ||
				record?.vendor_address?.address_line1;
			return (
				<Text style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
					{fallbackAddress || '-'}
				</Text>
			);
		},
	},
	{
		title: 'Coordinates',
		dataIndex: 'coordinates',
		key: 'coordinates',
		render: (coordinates, record) => {
			if (!record) return <Text>-</Text>;

			// Try to get from vendor_address or vendor_info
			const coords =
				coordinates ||
				record?.vendor_address?.coordinates ||
				record?.vendor_info?.address?.coordinates;
			if (!coords || typeof coords !== 'object' || (!coords.latitude && !coords.longitude)) {
				return <Text>-</Text>;
			}

			const lat = coords.latitude;
			const lng = coords.longitude;

			if (
				(lat === null || lat === undefined || Number.isNaN(parseFloat(lat))) &&
				(lng === null || lng === undefined || Number.isNaN(parseFloat(lng)))
			) {
				return <Text>-</Text>;
			}

			try {
				return (
					<Text style={{ fontSize: '12px' }}>
						{lat && lng
							? `${parseFloat(lat).toFixed(4)}°, ${parseFloat(lng).toFixed(4)}°`
							: '-'}
					</Text>
				);
			} catch (error) {
				console.error('Error parsing coordinates:', error);
				return <Text>-</Text>;
			}
		},
	},
	{
		title: 'Created at',
		dataIndex: 'created_at',
		key: 'created_at',
		sorter: true,
		render: (text) => {
			if (!text) return <Text>-</Text>;
			try {
				// Convert to IST if not already in IST format, format with AM/PM
				return (
					<Text>{moment.utc(text).utcOffset('+05:30').format('DD-MM-YYYY, h:mm a')}</Text>
				);
			} catch (error) {
				console.error('Error formatting date:', error);
				return <Text>-</Text>;
			}
		},
	},
	{
		title: 'Created by',
		dataIndex: 'created_by_name',
		key: 'created_by',
		render: (text, record) => {
			if (!record) return <Text>-</Text>;

			const createdBy = text || record?.created_by || '-';
			return <Text>{createdBy}</Text>;
		},
	},
	{
		title: 'Rating',
		dataIndex: 'rating',
		key: 'rating',
		sorter: true,
		render: (text) => {
			if (text === null || text === undefined) return <Text>-</Text>;
			try {
				const rating = parseFloat(text);
				if (Number.isNaN(rating)) return <Text>-</Text>;
				return <Text>{rating.toFixed(1)}</Text>;
			} catch (error) {
				console.error('Error formatting rating:', error);
				return <Text>-</Text>;
			}
		},
	},
	{
		title: 'Status',
		dataIndex: 'is_active',
		key: 'is_active',
		render: (is_active, record) => {
			if (!record) return <Text>-</Text>;
			return (
				<Switch
					checked={is_active === true || is_active === 1}
					checkedChildren="Active"
					unCheckedChildren="Inactive"
					onChange={(checked) => {
						if (handleStatusToggle && record) {
							handleStatusToggle(record, checked);
						}
					}}
				/>
			);
		},
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id, record) => {
			if (!id || !record) return <Text>-</Text>;
			if (!navigate || typeof navigate !== 'function') {
				console.error('Navigate function is not available');
				return <Text>-</Text>;
			}
			return (
				<ViewButton
					onClick={() => {
						if (id) {
							navigate(`/vendors/${id}`);
						}
					}}
				>
					<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
				</ViewButton>
			);
		},
	},
];
